import React, { useState, useEffect } from 'react';
import {
  User,
  FileText,
  Shield,
  Bell,
  PenSquare,
  Monitor,
  Smartphone,
  Hand,
  Sparkles
} from 'lucide-react';
import { useAuthProtection } from '../hooks/useAuthProtection';
import CandidateLayout from '../components/CandidateLayout';

/**
 * Account Settings Page
 * Matches reference design with full name, account, security, notifications, devices
 */

const Toggle = ({ checked, onChange }: { checked: boolean, onChange: () => void }) => (
  <button
    onClick={onChange}
    className={`w-12 h-6 rounded-full p-1 transition-colors duration-200 ease-in-out focus:outline-none ${
      checked ? 'bg-[#015FA6]' : 'bg-gray-300'
    }`}
  >
    <div className={`bg-white w-4 h-4 rounded-full shadow-sm transform transition-transform duration-200 ease-in-out ${
      checked ? 'translate-x-6' : 'translate-x-0'
    }`} />
  </button>
);

const AccountSettings: React.FC = () => {
  useAuthProtection();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [notifications, setNotifications] = useState({
    newJob: true,
    applicationResult: true,
    messages: false
  });

  // Get user info from localStorage
  useEffect(() => {
    const userDataString = localStorage.getItem('authUser');
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        setFirstName(userData.firstName || '');
        setLastName(userData.lastName || '');
        setEmail(userData.email || '');
      } catch (error) {
        console.error('Failed to parse user data:', error);
      }
    }
  }, []);

  const SectionHeader = ({ icon: Icon, title, action }: any) => (
    <div className="flex justify-between items-center mb-6">
      <div className="flex items-center gap-2">
        <Icon size={20} className="text-[#0B111E]" />
        <h3 className="text-lg font-bold text-[#0B111E]">{title}</h3>
      </div>
      {action}
    </div>
  );

  return (
    <CandidateLayout
      headerTitle="Account Settings"
      headerSubtitle="Updating your information will offer you the most relevant content"
    >
      {/* Content Area */}
      <div className="flex gap-6 flex-col xl:flex-row">

            {/* Left Column (Main Settings) */}
            <div className="flex-1 space-y-6">

              {/* Full Name */}
              <div className="bg-white rounded-2xl p-6 border border-[#EDEDED]">
                <SectionHeader
                  icon={User}
                  title="Full name"
                  action={<button className="p-2 hover:bg-gray-50 rounded-full"><PenSquare size={18} className="text-[#0B111E]"/></button>}
                />
                <div className="flex gap-6">
                  <div className="flex-1">
                    <label className="block text-sm font-bold text-[#0B111E] mb-2">First name</label>
                    <div className="text-sm text-[#757575] border-b border-[#EDEDED] pb-2">{firstName}</div>
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-bold text-[#0B111E] mb-2">Last name</label>
                    <div className="text-sm text-[#757575] border-b border-[#EDEDED] pb-2">{lastName}</div>
                  </div>
                </div>
              </div>

              {/* Account (Email) */}
              <div className="bg-white rounded-2xl p-6 border border-[#EDEDED]">
                <SectionHeader icon={FileText} title="Account" />
                <div className="flex justify-between items-center">
                  <div>
                    <label className="block text-sm font-bold text-[#0B111E] mb-1">Email Address</label>
                    <div className="text-xs text-[#757575]">{email || 'No email available'}</div>
                  </div>
                  <button className="px-4 py-2 border border-[#0B111E] rounded-lg text-xs font-bold text-[#0B111E] hover:bg-gray-50 transition-colors">
                    Reset Email
                  </button>
                </div>
              </div>

              {/* Security (Password) */}
              <div className="bg-white rounded-2xl p-6 border border-[#EDEDED]">
                <SectionHeader icon={Shield} title="Security" />
                <div className="flex justify-between items-center">
                  <div>
                    <label className="block text-sm font-bold text-[#0B111E] mb-1">Password</label>
                    <div className="text-sm text-[#757575] font-bold tracking-widest">••••••••••••••</div>
                  </div>
                  <button className="px-4 py-2 border border-[#0B111E] rounded-lg text-xs font-bold text-[#0B111E] hover:bg-gray-50 transition-colors">
                    Reset Password
                  </button>
                </div>
              </div>

              {/* Notification */}
              <div className="bg-white rounded-2xl p-6 border border-[#EDEDED]">
                <SectionHeader icon={Bell} title="Notification" />
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="text-sm font-bold text-[#0B111E] mb-1">New job</h4>
                      <p className="text-[10px] text-[#757575]">Notify me when an employer rejected me.</p>
                    </div>
                    <Toggle checked={notifications.newJob} onChange={() => setNotifications({...notifications, newJob: !notifications.newJob})} />
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="text-sm font-bold text-[#0B111E] mb-1">Application result</h4>
                      <p className="text-[10px] text-[#757575]">Notify me when an employer rejected me.</p>
                    </div>
                    <Toggle checked={notifications.applicationResult} onChange={() => setNotifications({...notifications, applicationResult: !notifications.applicationResult})} />
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="text-sm font-bold text-[#0B111E] mb-1">Messages</h4>
                      <p className="text-[10px] text-[#757575]">Notify me when an employer rejected me.</p>
                    </div>
                    <Toggle checked={notifications.messages} onChange={() => setNotifications({...notifications, messages: !notifications.messages})} />
                  </div>
                </div>
              </div>

              {/* Delete Account - sends email request instead of deleting */}
              <div className="flex justify-between items-start pt-4">
                <div className="max-w-lg">
                  <h4 className="text-sm font-bold text-[#0B111E] mb-2">Delete Account</h4>
                  <p className="text-[10px] text-[#757575] leading-relaxed">
                    If you'd like to delete your account, please reach out to us. We'd love to understand how we can improve.
                  </p>
                </div>
                <button
                  onClick={() => {
                    const subject = encodeURIComponent('Account Deletion Request');
                    const body = encodeURIComponent(`Hi Hush Team,\n\nI would like to request deletion of my account (${email}).\n\nReason for leaving:\n\n`);
                    window.location.href = `mailto:founders@hushtalent.io?subject=${subject}&body=${body}`;
                  }}
                  className="px-6 py-2.5 border border-[#0B111E] text-[#0B111E] text-xs font-bold rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                >
                  Request Deletion
                </button>
              </div>

            </div>

            {/* Right Column (Devices & Info) */}
            <div className="w-full xl:w-[400px] space-y-6">

              {/* Devices Card */}
              <div className="bg-white rounded-2xl p-6 border border-[#EDEDED] flex flex-col items-center text-center">
                <div className="mb-4 relative">
                  <Monitor size={48} strokeWidth={1.5} className="text-[#0B111E]" />
                  <Smartphone size={24} strokeWidth={1.5} className="text-[#0B111E] absolute -bottom-1 -right-2 bg-white rounded-full" />
                </div>
                <h3 className="text-lg font-bold text-[#0B111E] mb-8">Devices</h3>

                <div className="w-full text-left mb-6">
                  <h4 className="text-sm font-bold text-[#0B111E] mb-3">This device</h4>
                  <div className="flex items-center gap-2 text-sm text-[#0B111E]">
                    <Monitor size={16} /> Current browser
                  </div>
                </div>

                <button className="w-full flex items-center justify-center gap-2 text-[#FF5210] text-xs font-bold mb-8 hover:bg-red-50 py-2 rounded-lg transition-colors">
                  <Hand size={16} className="rotate-90" /> Terminate All Other Sessions
                </button>

                <div className="w-full text-left">
                  <h4 className="text-sm font-bold text-[#0B111E] mb-4">Active Devices</h4>
                  <div className="text-xs text-[#757575]">No other active sessions</div>
                </div>
              </div>


            </div>
          </div>

        {/* Floating Action Button (Star) */}
        <div className="fixed bottom-6 right-6">
          <button className="w-14 h-14 bg-[#E3F2FD] rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform relative">
              <Sparkles size={24} className="text-[#015FA6] fill-[#015FA6]" />
              <div className="absolute top-0 right-0 w-5 h-5 bg-[#FF5210] rounded-full text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">3</div>
          </button>
        </div>
    </CandidateLayout>
  );
};

export default AccountSettings;
